import type { BusinessSummary, SavedScan, ScanResult, ScannedPage } from '@/services/aiPreview';

/** A scan the page can use: the AI pilot's saved one, or one run here (never stored). */
export interface ScanEntry {
  source: 'saved' | 'preview';
  url: string;
  status: string;
  scannedAt: string | null;
  summary: BusinessSummary | null;
  pages: ScannedPage[];
  error: string | null;
}

export const COMPANY_KEY = 'company';

export function entryFromSaved(saved: SavedScan): ScanEntry {
  return { source: 'saved', ...saved };
}

/** For a failed request whose response had no message (a gateway timeout, say), which the API client doesn't show. */
export function failedWithoutMessage(err: unknown): boolean {
  return !(err as { response?: { data?: { message?: string } } }).response?.data?.message;
}

export function entryFromResult(result: ScanResult): ScanEntry {
  return {
    source: 'preview',
    url: result.url,
    status: result.status,
    scannedAt: result.scannedAt,
    summary: result.status === 'done' ? result.summary : null,
    pages: result.pages,
    error: result.status === 'failed' ? result.errorMessage : null,
  };
}

/** "https://go.acme.com/hip/" -> "go.acme.com/hip" */
export function displayUrl(url: string): string {
  return url.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
}

export const STAGE_LABELS: Record<string, string> = {
  'signed_up:wizard': 'Signed up, in the wizard',
  'signed_up:plans': 'Signed up, picking a plan',
  'signed_up:checkout': 'Signed up, at checkout',
  onboarded: 'Onboarded',
  activated: 'Activated',
  value_realized: 'Getting leads',
  power_user: 'Power user',
  churning: 'Cancelling',
  churned: 'Churned',
};

export const TRIAL_RISK_LABELS: Record<string, { label: string; detail: string; warn: boolean }> = {
  at_risk_no_leads: { label: 'No leads yet', detail: 'Under 3 submissions', warn: true },
  at_risk_no_bookings: { label: 'No bookings yet', detail: 'Leads but no bookings', warn: true },
  on_track: { label: 'On track', detail: 'Leads and bookings', warn: false },
};

export function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : s;
}

export function wordCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}
